import { TARIF_TYPE } from "Achat/Constants";
import moment from "moment";

/**
* Utilitaire contenant des fonctions statiques pour la gestion des achats
* @category Utils
* @hideconstructor
*/
class AchatUtils {
  /**
    * Fonction permettant de récupérer la tranche de prix pour chaque quantité de service
    * @param {object} service Le service pour lequel on veut récupérer les tranches de prix
    * @returns {array} Tableau des tranches de prix avec les quantités et les prix
  */
  static getCurrentTranche(tarif, quantite) {
    // Si une seule tranche ou pas de quantité
    if ((tarif && tarif.tranches.length === 1)) {
      return tarif.tranches[0];
    }

    // Sinon recherche de la bonne tranche de tarif
    // On récupère la date de validité avec la valeur
    const dateValidite = (tarif.idTarifType === TARIF_TYPE.TEMPS && quantite) > 1
      ? moment().add(quantite * tarif.secondes, "second").format()
      : moment().format()


    // On filtre les tarifs qui sont dans la date / période de validité
    let tranchesFilter = [];
    tranchesFilter = tarif.tranches.filter(item => {
      // Recherche des tranches entre la date de début et fin

      if (moment(dateValidite).isBetween(item.dateDebut, item.dateFin)) {
        const periodeDeb = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeDebut, 'DD/MM/YYYY HH:mm').format();
        const periodeFin = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeFin, 'DD/MM/YYYY HH:mm').format();

        // Recherche dans la période
        return moment(dateValidite).isBetween(periodeDeb, periodeFin);
      }

      return false;
    });

    // on renvoie le bon item dans la liste
    let tranche = tranchesFilter.length === 1 || !quantite
      ? tranchesFilter[0]
      : tranchesFilter.find(item => (quantite <= item.uniteMax) && (quantite >= item.uniteMin))

    // Fallback uniquement si la quantité dépasse le max de toutes les tranches (ex. 25 ou 30 pour paliers 1,6,12,16,24)
    if (!tranche && quantite && tranchesFilter.length > 0) {
      const maxUnite = Math.max(...tranchesFilter.map(t => t.uniteMax));
      if (quantite > maxUnite) {
        const tranchesSorted = [...tranchesFilter].sort((a, b) => b.uniteMax - a.uniteMax);
        tranche = tranchesSorted[0];
      }
    }

    return tranche;
  };

  /**
   * Retourne la liste des quantités valides (définies par au moins une tranche) pour un tarif.
   * Utilisé pour faire sauter les quantités non définies dans le sélecteur (+/-).
   * @param {object} tarif Tarif avec tranches (même filtre date/période que getCurrentTranche)
   * @returns {number[]} Quantités valides triées, ex. [1, 6, 12, 16, 24]
   */
  static getValidQuantites(tarif) {
    if (!tarif?.tranches?.length) return [];
    if (tarif.tranches.length === 1) {
      const t = tarif.tranches[0];
      const min = t.uniteMin ?? 0;
      const max = t.uniteMax ?? min;
      const list = [];
      for (let q = min; q <= max; q++) list.push(q);
      return list;
    }
    const dateValidite = moment().format();
    const tranchesFilter = tarif.tranches.filter(item => {
      if (!moment(dateValidite).isBetween(item.dateDebut, item.dateFin)) return false;
      const periodeDeb = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeDebut, 'DD/MM/YYYY HH:mm').format();
      const periodeFin = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeFin, 'DD/MM/YYYY HH:mm').format();
      return moment(dateValidite).isBetween(periodeDeb, periodeFin);
    });
    const set = new Set();
    tranchesFilter.forEach(item => {
      const min = item.uniteMin ?? 0;
      const max = item.uniteMax ?? min;
      for (let q = min; q <= max; q++) set.add(q);
    });
    return Array.from(set).sort((a, b) => a - b);
  }

  /**
   * Fonction qui renvoie les numéros à la fin d'une chaîne. Utile pour récupérer les numéros de service
   * @param {string} chaine
   * @returns {string}
   */
  static getStringEndingNumbers(chaine) {
    const regex = /(\d+)$/;

    // Recherche des correspondances
    const match = chaine.match(regex);

    // Retourne la chaîne de chiffres trouvée ou null si aucun nombre n'est trouvé
    return match ? match[1] : null;
  }

  //AYB 26.08.25 tarif a plusieurs paliers ponctuels (1h/3h/6h...) ; 1 seule tranche (eau, parking) = false
  static isForfaitTarif(tarif) {
    if (!tarif?.tranches || tarif.tranches.length <= 1) {
      return false;
    }
    return tarif.tranches.every((t) => t.uniteMin === t.uniteMax);
  }

  /**
    * Fonction permettant de récupérer la tranche de prix pour chaque quantité de service
    * @param {object} service Le service pour lequel on veut récupérer les tranches de prix
    * @returns {array} Tableau des tranches de prix avec les quantités et les prix
  */
  static getTranches(service) {
    //AYB 26.08.25 paliers forfait : une ligne avec la quantite achetee (6h -> 6) pour lancer la duree reelle ; le montant UI reste T1+T2+T3
    if (AchatUtils.isForfaitTarif(service?.tarif) && service.quantite) {
      const actualTranche = getCurrentTranche(service.tarif, service.quantite);
      if (!actualTranche) {
        return [];
      }
      return [{
        idTranche: actualTranche.idTranche,
        quantite: service.quantite,
        prix: actualTranche.prix,
      }];
    }

    let tranches = [];

    for (let i = 1; i <= service.quantite; i++) {
      const actualTranche = getCurrentTranche(service.tarif, i);
      if (!actualTranche) continue; // ne pas compter les quantités non définies dans une tranche

      if (!tranches.find(item => item.idTranche === actualTranche.idTranche)) {
        tranches.push({
          idTranche: actualTranche.idTranche,
          quantite: 1,
          prix: actualTranche.prix,
        });
      } else {
        const index = tranches.findIndex(item => item.idTranche === actualTranche.idTranche);
        tranches[index].quantite = tranches[index].quantite + 1;
      }
    }
    return tranches;
  }
}

export const getCurrentTranche = AchatUtils.getCurrentTranche;
export const getStringEndingNumbers = AchatUtils.getStringEndingNumbers;
export const getTranches = AchatUtils.getTranches;
export const getValidQuantites = AchatUtils.getValidQuantites;
